import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { get as _get } from 'lodash';

import { optionalAttribute } from 'utils/jsx';
import { locale } from 'utils/locale';
import localize from 'components/Localize/Localize';

import ProductAvailability from 'components/ProductAvailability/ProductAvailability';
import QuantitySelector from 'components/widgets/QuantitySelector/QuantitySelector';

import styles from './MobileProductSelector.css';
import translations from 'components/ProductAvailability/translations';

const cx = classNames.bind(styles);

class MobileProductSelector extends React.Component {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    window.addEventListener(`click`, this.handleClick);
  }

  componentWillUnmount() {
    window.removeEventListener(`click`, this.handleClick);
  }

  handleClick(e) {
    if (!this.containerEl.parentElement.contains(e.target) && this.props.isOpen && !e.target.className.includes(`optional--limited`)) {
      this.props.hideMobileProductSelect();
    }
  }

  renderOptional(optional) {
    if (!optional) {
      return null;
    }

    return Object.keys(optional).map((key) => {
      if (key === `readyToShip`) {
        return null;
      }

      return (
        <span
          className={cx(`optional`, `optional--${key}`)}
          key={key}
        >
          {optional[key]}
        </span>
      );
    });
  }

  renderShipsBy(isSelected, option) {
    const readyToShip = _get(option, `optional.readyToShip`);

    if (!isSelected && readyToShip && this.props.showLimited) {
      return <span className={styles[`optional--limited`]}>{`${this.props.t.limited}`}</span>;
    }

    if (!this.props.shipsBy || !isSelected) {
      return;
    }

    return (
      <ProductAvailability
        className={styles[`optional--ships-by`]}
        hideHolidayShipping={true}
        {...this.props.shipsBy}
      />
    );
  }

  renderOptions() {
    return this.props.options.map((option, i) => {
      const fn = () => this.props.onClick(option.value);
      const onClick = optionalAttribute(`onClick`, fn, this.props.onClick);
      const isSelect = i === 0 && !option.value;
      const isSelected = option.value === this.props.selectedValue;
      const optionClass = cx(`option`, {
        'option--selected': isSelected,
        'option--stacked': locale(this.props.locale).isFrCA(),
      });

      if (isSelect) {
        return (
          <li
            className={styles[`option--select`]}
            key={option.value}
          >
            <div className={styles[`option-inner`]}>
              {option.label}
            </div>
          </li>
        );
      }

      return (
        <li
          className={optionClass}
          key={option.value}
          {...onClick}
        >
          <div className={styles[`option-inner`]}>
            <div className={styles[`label-wrapper`]}>
              <span className={styles[`label`]}>
                {option.label}
              </span>
              {this.renderShipsBy(isSelected, option)}
            </div>
            {this.renderOptional(option.optional)}
          </div>
        </li>
      );
    });
  }

  renderQuantitySelector() {
    if (!this.props.showQuantitySelector) {
      return null;
    }

    return (
      <div className={styles[`quantity-selector-outer`]}>
        <div className={styles[`quantity-selector-inner`]}>
          <span>{`${this.props.t.quantity}:`}</span>
          <div className={styles[`quantity-selector-container`]}>
            <QuantitySelector
              defaultQuantity={this.props.quantity}
              onChange={this.props.handleQuantityChange}
              quantity={this.props.quantity}
              ref={ (el) => { this.quantitySelector = el; } }
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const containerClass = classNames(styles[`container`], {
      [styles[`container--open`]]: this.props.isOpen,
      [styles[`container--unselected`]]: this.props.isUnselected,
      [this.props.containerStyles]: this.props.containerStyles,
    });

    return (
      <div
        className={containerClass}
        ref={(el) => { this.containerEl = el; }}
      >
        <button
          className={styles[`close`]}
          onClick={this.props.hideMobileProductSelect}
        >
          &times;
        </button>
        <ul className={styles[`options`]}>
          {this.renderOptions()}
        </ul>
        {this.renderQuantitySelector()}
      </div>
    );
  }
}

MobileProductSelector.defaultProps = {
  showLimited: false,
};

MobileProductSelector.propTypes = {
  containerStyles: PropTypes.string,
  handleQuantityChange: PropTypes.func,
  isOpen: PropTypes.bool,
  isUnselected: PropTypes.bool,
  onClick: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    optional: PropTypes.object,
  })),
  quantity: PropTypes.number,
  selectedValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  shipsBy: PropTypes.shape({
    date: PropTypes.string,
    isReadyToShip: PropTypes.bool.isRequired,
  }),
  t: PropTypes.shape({
    limited: PropTypes.string,
  }),
};

const mapStateToProps = (state) => ({
  isOpen: _get(state, `UI.mobileProductSelectShown`),
});

export default connect(mapStateToProps)(localize(MobileProductSelector, translations));
